import React, { useState } from "react";
import Box from "@material-ui/core/Box";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import IconButton from "@material-ui/core/IconButton";
import LinearProgress from "@material-ui/core/LinearProgress";
import ToggleButton from "@material-ui/lab/ToggleButton";
import ToggleButtonGroup from "@material-ui/lab/ToggleButtonGroup";
import CloseIcon from "@material-ui/icons/Close";
import { DateTime } from "luxon";
import { makeStyles, createMuiTheme, ThemeProvider } from "@material-ui/core/styles";
import {
  DESCS,
  PERIODS,
  P,
  CSS_VARS,
  TABS,
  INSIGHTS_PERIOD_DAYS,
} from "./dashboard/constants";
import OverviewTab from "./dashboard/OverviewTab";
import LeakageTab from "./dashboard/LeakageTab";
import ChannelsTab from "./dashboard/ChannelsTab";
import StaffTab from "./dashboard/StaffTab";
import NetworkTab from "./dashboard/NetworkTab";
import ArrivalsTab from "./dashboard/ArrivalsTab";
import OperationsTab from "./dashboard/OperationsTab";
import { PermissionOnly } from "../../components/AdminOnly";
import { downloadWithAuth, PERMISSIONS } from "../../lib/authManager";

const DRIVO_URL = process.env.REACT_APP_API_URL || "";

const insightsTheme = createMuiTheme({
  typography: { fontFamily: "var(--font-sans)" },
});

const DEFAULT_TIME_ZONE = "Australia/Sydney";
const AS_OF_OPTIONS = Array.from({ length: 48 }, (_, i) => `${String(Math.floor(i / 2)).padStart(2, '0')}:${i % 2 ? '30' : '00'}`);
AS_OF_OPTIONS.push('23:59');

function currentAsOf(now, timeZoneName) {
  const local = DateTime.fromJSDate(now).setZone(timeZoneName || DEFAULT_TIME_ZONE);
  return `${local.toFormat('HH')}:${local.minute < 30 ? '00' : '30'}`;
}

function zonedDateTime(now, timeZoneName) {
  return DateTime.fromJSDate(now).setZone(timeZoneName || DEFAULT_TIME_ZONE).startOf("day");
}

export function getInsightsDateRange(
  period,
  customFrom,
  customTo,
  now = new Date(),
  timeZoneName = DEFAULT_TIME_ZONE,
) {
  const zone = timeZoneName || DEFAULT_TIME_ZONE;
  if (period === "day" || period === "custom") {
    const startDate = customFrom;
    // Date inputs are inclusive. Normalize them to the half-open interval used
    // by the API so the selected end day is queried in full.
    const selectedEndDate = period === "day" ? customFrom : customTo;
    const endDate = DateTime.fromISO(selectedEndDate, { zone })
      .plus({ days: 1 })
      .toISODate();
    const days = daysBetween(startDate, endDate);
    const comparisonEnd = DateTime.fromISO(startDate, { zone });
    const comparisonStart = comparisonEnd.minus({ days });
    return {
      startDate,
      endDate,
      comparisonStartDate: comparisonStart.toISODate(),
      comparisonEndDate: comparisonEnd.toISODate(),
    };
  }

  const dayCount = INSIGHTS_PERIOD_DAYS[period] || INSIGHTS_PERIOD_DAYS.MTD;
  let end = zonedDateTime(now, zone).plus({ days: 1 });
  if (period === "YESTERDAY") end = end.minus({ days: 1 });
  const start = end.minus({ days: dayCount });
  const comparisonEnd = start;
  const comparisonStart = comparisonEnd.minus({ days: dayCount });

  return {
    startDate: start.toISODate(),
    endDate: end.toISODate(),
    comparisonStartDate: comparisonStart.toISODate(),
    comparisonEndDate: comparisonEnd.toISODate(),
  };
}

function daysBetween(fromDate, toDate) {
  const from = DateTime.fromISO(fromDate, { zone: "UTC" });
  const to = DateTime.fromISO(toDate, { zone: "UTC" });
  return Math.max(1, Math.floor(to.diff(from, "days").days));
}

function clampDateInput(value, min, max) {
  if (!value) return value;
  if (value < min) return min;
  if (value > max) return max;
  return value;
}

function formatDateLabel(value) {
  return new Date(`${value}T00:00:00`).toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export function formatSelectedDateRange(startDate, endDate) {
  const start = DateTime.fromISO(startDate);
  const end = DateTime.fromISO(endDate);
  if (!start.isValid || !end.isValid) return `${startDate} to ${endDate}`;
  if (start.hasSame(end, "day")) return start.toFormat("d LLL yyyy");
  if (start.hasSame(end, "month")) {
    return `${start.day} to ${end.toFormat("d LLL yyyy")}`;
  }
  return `${start.toFormat("d LLL yyyy")} to ${end.toFormat("d LLL yyyy")}`;
}

const useStyles = makeStyles({
  root: {
    ...CSS_VARS,
    background: "var(--color-background-primary)",
    fontFamily: "var(--font-sans)",
    color: "var(--color-text-primary)",
    display: "flex",
    flexDirection: "column",
  },
  titleBar: { padding: "44px 24px 16px" },
  title: { fontWeight: 700, color: "var(--color-text-primary)" },
  content: { padding: "16px 24px", flex: 1 },
  comparing: { color: "var(--color-text-tertiary)" },
  toolbar: {
    padding: "10px 24px",
    background: "var(--color-background-secondary)",
    borderBottom: "1px solid var(--color-border-tertiary)",
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    gap: 8,
    flexShrink: 0,
    boxSizing: "border-box",
    marginLeft: 24,
    marginRight: 24,
  },
  controlsRow: { display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8 },
  asOfControl: { width: 150 },
  downloadBtn: {
    marginLeft: "auto",
    textTransform: "none",
    color: "#fff",
    background: P.nv,
    width: 152,
    whiteSpace: "nowrap",
    "&:hover": { background: P.pd },
  },
  periodGroup: { display: "flex", flexWrap: "wrap" },
  periodBtn: {
    textTransform: "none",
    width: "auto",
    minWidth: 0,
    paddingLeft: 12,
    paddingRight: 12,
    whiteSpace: "nowrap",
    "&.Mui-selected": { backgroundColor: "#3127B8", color: "#fff" },
    "&.Mui-selected:hover": { backgroundColor: "#241C8A" },
  },
  applyBtn: {
    textTransform: "none",
    backgroundColor: "#3127B8",
    color: "#fff",
    "&:hover": { backgroundColor: "#241C8A" },
  },
  dateRange: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 8,
    padding: "8px 12px",
  },
  pickerHelp: { color: "var(--color-text-tertiary)", width: "100%" },
  comparisonRow: { display: "flex", alignItems: "center", minHeight: 20 },
  overviewProgress: { height: 4, margin: "0 -24px -10px" },
});

export default function InsightsDashboard({
  tab = "overview",
  setTab,
  enabledTabs,
  installation,
  facility,
  now = new Date(),
}) {
  const cls = useStyles();
  const installationToday = zonedDateTime(now, installation?.timeZoneName);
  const today = installationToday.toISODate();
  const sixMonthsAgo = installationToday.minus({ months: 6 }).toISODate();
  const [period, setPeriod] = useState("MTD");
  const [picker, setPicker] = useState(null);
  const [customFrom, setCustomFrom] = useState(sixMonthsAgo);
  const [customTo, setCustomTo] = useState(today);
  const [appliedFrom, setAppliedFrom] = useState(sixMonthsAgo);
  const [appliedTo, setAppliedTo] = useState(today);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [asOf, setAsOf] = useState(() => currentAsOf(now, installation?.timeZoneName));
  const visibleTabs = enabledTabs?.length
    ? TABS.filter((item) => enabledTabs.includes(item.id))
    : TABS;
  const activeTab = visibleTabs.some((item) => item.id === tab)
    ? tab
    : visibleTabs[0]?.id;

  if (!activeTab) return null;

  const hasPeriod = !["arrivals","ops"].includes(activeTab);

  const handleTab = t => {
    if (setTab && visibleTabs.some((item) => item.id === t)) setTab(t);
  };
  const handlePeriod = p => { setPeriod(p); setPicker(null); };
  const handleCustomFrom = (value) => {
    const nextFrom = clampDateInput(value, sixMonthsAgo, customTo);
    setCustomFrom(nextFrom);
  };
  const handleCustomTo = (value) => {
    const nextTo = clampDateInput(value, customFrom, today);
    setCustomTo(nextTo);
  };
  const handleCustomApply = () => {
    const nextFrom = clampDateInput(customFrom, sixMonthsAgo, customTo);
    const nextTo = clampDateInput(customTo, nextFrom, today);
    setCustomFrom(nextFrom);
    setCustomTo(nextTo);
    setAppliedFrom(nextFrom);
    setAppliedTo(nextTo);
    setPeriod("custom");
    setPicker(null);
  };
  const customDays = daysBetween(
    appliedFrom,
    DateTime.fromISO(appliedTo).plus({ days: 1 }).toISODate(),
  );
  const overviewDateRange = getInsightsDateRange(
    period,
    appliedFrom,
    appliedTo,
    now,
    installation?.timeZoneName,
  );
  const includesToday = overviewDateRange.startDate <= today && overviewDateRange.endDate > today;
  const effectiveAsOf = includesToday ? asOf : '23:59';
  const downloadOverview = async () => {
    const params = new URLSearchParams({
      facilityId: facility.id,
      ...overviewDateRange,
      asOf: effectiveAsOf,
    });
    const lastDay = DateTime.fromISO(overviewDateRange.endDate).minus({ days: 1 }).toISODate();
    await downloadWithAuth({
      url: `${DRIVO_URL}/insights/overview/download?${params.toString()}`,
      filename: `drivo-insights-overview_${overviewDateRange.startDate}_${lastDay}.xlsx`,
    });
  };
  const chartPeriod = PDPeriod(period);

  const periodLabel = period === "custom"
    ? `${formatSelectedDateRange(appliedFrom, appliedTo)} vs prior equivalent`
    : period === "day"
      ? `${formatSelectedDateRange(appliedFrom, appliedFrom)} vs previous day`
      : (DESCS[period] || DESCS.MTD).toLowerCase();

  const title = (visibleTabs.find(t => t.id === activeTab) || {}).label || "Overview";

  return (
    <ThemeProvider theme={insightsTheme}>
      <Box className={cls.root}>
        <Box className={cls.titleBar}>
          <Typography variant="h5" component="span" className={cls.title}>{title}</Typography>
        </Box>
        <div>
          {hasPeriod && (
            <Box className={cls.toolbar}>
              <Box className={cls.controlsRow}>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={picker || period}
                  className={cls.periodGroup}
                  onChange={(event, value) => value && !["day", "custom"].includes(value) && handlePeriod(value)}
                >
                  {PERIODS.map((item) => (
                    <ToggleButton key={item} value={item} className={cls.periodBtn}>
                      {item === "TODAY" ? "Today" : item === "YESTERDAY" ? "Yesterday" : item}
                    </ToggleButton>
                  ))}
                  <ToggleButton
                    value="day"
                    className={cls.periodBtn}
                    onClick={() => setPicker(picker === "day" ? null : "day")}
                  >
                    Day
                  </ToggleButton>
                  <ToggleButton
                    value="custom"
                    className={cls.periodBtn}
                    onClick={() => setPicker(picker === "custom" ? null : "custom")}
                  >
                    Custom Range
                  </ToggleButton>
                </ToggleButtonGroup>
                {activeTab === "overview" && (
                  <><TextField
                    select
                    variant="outlined"
                    size="small"
                    id="insights-as-of"
                    label="As of"
                    value={effectiveAsOf}
                    disabled={!includesToday}
                    onChange={(event) => setAsOf(event.target.value)}
                    InputLabelProps={{ shrink: true }}
                    className={cls.asOfControl}
                  >
                    {AS_OF_OPTIONS.map((time) => (
                      <MenuItem key={time} value={time}>{time === '23:59' ? 'End of day' : time}</MenuItem>
                    ))}
                  </TextField>
                  <PermissionOnly permissions={PERMISSIONS.REPORTS_EXPORT}>
                    <Button className={cls.downloadBtn} variant="contained" disableElevation
                      disabled={!facility?.id || overviewLoading} onClick={downloadOverview}>
                      Download XLSX
                    </Button>
                  </PermissionOnly></>
                )}
              </Box>
              {activeTab === "overview" && (
                <Typography variant="caption" className={cls.pickerHelp}>
                  {includesToday
                    ? `Last days in both periods include data until ${effectiveAsOf} (${installation?.timeZoneName || DEFAULT_TIME_ZONE}).`
                    : 'Full day data is selected for both periods.'}
                </Typography>
              )}
              {picker && (
                <Paper variant="outlined" className={cls.dateRange}>
                  {picker === "custom" && (
                    <TextField
                      variant="outlined"
                      type="date"
                      label="From"
                      size="small"
                      value={customFrom}
                      onChange={(event) => handleCustomFrom(event.target.value)}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ min: sixMonthsAgo, max: customTo }}
                    />
                  )}
                  <TextField
                    variant="outlined"
                    type="date"
                    label={picker === "day" ? "Date" : "To"}
                    size="small"
                    value={picker === "day" ? customFrom : customTo}
                    onChange={(event) => picker === "day"
                      ? setCustomFrom(event.target.value)
                      : handleCustomTo(event.target.value)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: picker === "day" ? sixMonthsAgo : customFrom, max: today }}
                  />
                  <Button
                    size="small"
                    variant="contained"
                    disableElevation
                    className={cls.applyBtn}
                    onClick={() => {
                      if (picker === "day") {
                        const nextDate = clampDateInput(customFrom, sixMonthsAgo, today);
                        setCustomFrom(nextDate);
                        setCustomTo(nextDate);
                        setAppliedFrom(nextDate);
                        setAppliedTo(nextDate);
                        setPeriod("day");
                        setPicker(null);
                      } else {
                        handleCustomApply();
                      }
                    }}
                  >
                    Apply
                  </Button>
                  <IconButton size="small" aria-label="close" onClick={() => setPicker(null)}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                  <Typography variant="caption" className={cls.pickerHelp}>
                    Data from {formatDateLabel(sixMonthsAgo)} through the selected end date. {picker === "day"
                      ? "Compared with the previous day."
                      : "Compared with the same-length period immediately before."}
                  </Typography>
                </Paper>
              )}
              <Box className={cls.comparisonRow}>
                <Typography variant="body2" className={cls.comparing}>Comparing {periodLabel}</Typography>
              </Box>
              {activeTab === "overview" && overviewLoading && (
                <LinearProgress
                  className={cls.overviewProgress}
                  aria-label="Loading updated insights"
                />
              )}
            </Box>
          )}

          <Box className={cls.content}>
            {activeTab === "overview" && (
              <OverviewTab
                period={period}
                customDays={customDays}
                startDate={overviewDateRange.startDate}
                endDate={overviewDateRange.endDate}
                comparisonStartDate={overviewDateRange.comparisonStartDate}
                comparisonEndDate={overviewDateRange.comparisonEndDate}
                asOf={effectiveAsOf}
                setTab={handleTab}
                availableTabs={visibleTabs.map((item) => item.id)}
                onLoadingChange={setOverviewLoading}
              />
            )}
            {activeTab === "leakage" && <LeakageTab period={chartPeriod} />}
            {activeTab === "channels" && <ChannelsTab period={chartPeriod} />}
            {activeTab === "staff" && <StaffTab period={chartPeriod} />}
            {activeTab === "network" && <NetworkTab period={chartPeriod} />}
            {activeTab === "arrivals" && <ArrivalsTab />}
            {activeTab === "ops" && <OperationsTab />}
          </Box>
        </div>
      </Box>
    </ThemeProvider>
  );
}

function PDPeriod(period) {
  return ["1W", "MTD", "QTD"].includes(period) ? period : "MTD";
}
